import React, { useState, useEffect, useRef } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSearch } from "@fortawesome/free-solid-svg-icons";
import GradientBtn from "./GradientBtn";
import httpRequest from "src/helpers/httpRequest";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import CircularProgress from "@mui/material/CircularProgress";
import Cookies from "js-cookie";
import {
  customer,
  doctor,
  doctorMode,
  ImageBaseUrl,
  QueryPath,
} from "src/lib/constans";
import defaultProfile from "src/assets/images/default_profile_image.png";
import defaultShoppingImage from "src/assets/images/customer-dashboard/shoppingbag.png";
import {
  convertToKebabCase,
  parseQueryParams,
} from "src/helpers/helperFunctions";

interface props {
  context: string;
  style?: React.CSSProperties;
  className?: string;
  placeholder?: string;
}

const WebGlobalSearch: React.FC<props> = ({
  context,
  style,
  className,
  placeholder,
}) => {
  const navigate = useNavigate();
  const [searchVal, setSearchVal] = useState<any>("");
  const [searchedData, setSearchedData] = useState<any>();
  const [isLoader, setIsLoader] = useState(false);
  const [fetchTimer, setFetchTimer] = useState<NodeJS.Timeout | null>(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const searchRef = useRef<HTMLFormElement>(null);
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        searchRef.current &&
        !searchRef.current.contains(event.target as Node)
      ) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);
  const fetchedData = async (val: any) => {
    setIsLoader(true);
    const { res, err } = await httpRequest<any>({
      method: "get",
      path: `/home-care-services/mega/search?${parseQueryParams({
        searchTerm: val,
      })}`,
    });
    if (res) {
      setSearchedData(res);
    } else {
      toast(err?.message, { type: "error" });
    }
    setIsLoader(false);
  };
  const handleSuggestionClick = (itemName: string) => {
    setSearchVal(itemName);
    fetchedData(itemName);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchVal(value);
    setIsDropdownOpen(!!value);

    setIsLoader(true);
    if (fetchTimer) clearTimeout(fetchTimer);
    if (value) {
      const timer = setTimeout(() => {
        fetchedData(value);
      }, 1000);
      setFetchTimer(timer);
    } else {
      setIsLoader(false);
      setSearchedData("");
    }
  };

  const routePrefix = context === "dashboard" ? "/customer" : "";

  const handleNavigate = (section: string) => {
    let route = "";

    switch (section) {
      case "pharmacy":
        route = `/category?${parseQueryParams({
          [QueryPath.search]: searchVal,
        })}`;
        break;
      case "homecare":
        route = `/home-care?${parseQueryParams({
          [QueryPath.search]: searchVal,
        })}`;
        break;
      case "diagnostic lab test":
        route = `/diagnostic-products?${parseQueryParams({
          [QueryPath.search]: searchVal,
        })}`;
        break;
      case "diagnostic packages":
        route = `/health-packages?${parseQueryParams({
          [QueryPath.search]: searchVal,
        })}`;
        break;
      case "doctor":
        route = `/consult-a-doctor?${parseQueryParams({
          [QueryPath.search]: searchVal,
        })}`;
        break;
      default:
        toast("Invalid section", { type: "error" });
        return;
    }

    navigate(route || "/");
    setSearchedData("");
    setSearchVal("");
    setIsDropdownOpen(false);
  };
  const handleProductNavigate = (item: any) => {
    if (!item || !item._id) return;

    let route = "";
    switch (item.type) {
      case "pharmacy":
        route = `/pharmacy-products/${item._id}/view`;
        break;
      case "homecare":
        route = `/homecare-services/${item._id}/view`;
        break;
      case "diagnosticProduct":
        route = `/diagnostic-product/test/${item._id}/view`;
        break;
      case "diagnosticPackage":
        route = `/diagnostic-product/package/${item._id}/view`;
        break;
      case "doctor":
        route = `/doctor-public-profile/${item._id}/view`;
        break;
      case "brand":
        route = `/brand/${convertToKebabCase(item?.name)}?${parseQueryParams({
          [QueryPath.brand]: item?._id,
          [QueryPath.brandName]: item?.name,
        })}`;
        break;
      case "mainCategory":
        route = `/category/${convertToKebabCase(item?.name)}?${parseQueryParams({
          [QueryPath.mainCategory]: item?._id,
          [QueryPath.mainCategoryName]: item?.name,
        })}`;
        break;
      default:
        toast("Unknown product type", { type: "error" });
        return;
    }

    navigate(route || "/");
    setSearchedData("");
    setSearchVal("");
    setIsDropdownOpen(false);
  };

  return (
    <form
      ref={searchRef}
      className={`search-main relative h-fit 2xl:py-2 py-1 lg:py-1.5 px-2 lg:px-3 ${className}`}
      style={context === "dashboard" ? { width: "25vw", ...style } : {}}
    >
      <div className="relative h-full flex items-center justify-center">
        <FontAwesomeIcon
          icon={faSearch}
          className="text-[#707070] font-size-19px"
        />
      </div>
      <input
        value={searchVal}
        onChange={handleInputChange}
        className="font-Poppins-Regular flex-1 font-size-18px text-[#707070] placeholder:text-[#707070]"
        placeholder={placeholder || "Search products, brands and categories"}
        type="Search"
        onFocus={() => {
          if (searchVal) {
            setIsDropdownOpen(true);
          }
        }}
        id="web-global-search-input"
      />
      {/* <GradientBtn extraClass='gradient-btn-margin-none' ButtonText='Search' /> */}
      {isDropdownOpen && (
        <div className="absolute left-0 z-10 bg-white w-full max-h-[35vw] top-full mt-0.5 overflow-y-auto scrollbar-5 rounded-md ">
          <div className="flex flex-row gap-1 2xl:p-3 lg:p-2.5 p-2 bg-[#efefef]">
            <h4 className="text-[#707070] font-Poppins-Regular  font-size-16px">
              Showing result for{" "}
            </h4>
            <span className="theme-color-green font-Poppins-Medium font-size-16px">
              {searchVal}
            </span>
          </div>

          {isLoader ? (
            <div className="flex justify-center items-center p-4">
              <CircularProgress style={{ width: 20, height: 20 }} />
            </div>
          ) : (searchedData?.suggestions && searchedData?.suggestions) ||
            searchedData?.suggestions.length ||
            searchedData?.suggestionsDoc?.length ? (
            <>
              {[
                ...searchedData?.suggestions,
                ...searchedData?.suggestionsDoc,
              ].map((item: any, index: any) => (
                <div
                  key={index}
                  onClick={() => {
                    handleSuggestionClick(item?.name);
                  }}
                  className={`flex flex-row gap-2 items-center 2xl:p-3 lg:p-2.5 p-2 cursor-pointer border-t border-[#d3d3d3]`}
                  id={`web-global-search-suggestion-item-${index + 1}`}
                >
                  <FontAwesomeIcon
                    icon={faSearch}
                    className="text-[#707070]  font-size-16px"
                  />
                  <span className="text-[#707070] font-Poppins-Medium font-size-16px">
                    {highlightMatch(item.name, searchVal)}
                  </span>
                </div>
              ))}
              {searchedData?.mainCategory && searchedData?.mainCategory.length > 0 && (
                <div className="flex flex-row justify-between 2xl:p-3 lg:p-2.5 p-2 bg-[#efefef]">
                  <h4 className=" font-Poppins-SemiBold  font-size-16px">
                    Categories{" "}
                  </h4>
                  <button
                    type="button"
                    onClick={() => handleNavigate("pharmacy")}
                    className="theme-color-green font-Poppins-Medium font-size-16px underline"
                    id={`web-global-search-view-all-button-pharmacy`}
                  >
                    View all
                  </button>
                </div>
              )}

              {searchedData?.mainCategory &&
                searchedData?.mainCategory.length > 0 &&
                searchedData?.mainCategory.slice(0, 2).map((item: any, index: any) => (
                  <div
                    key={index}
                    onClick={() =>
                      handleProductNavigate({ ...item, type: "mainCategory" })
                    }
                    className={`flex flex-row gap-2 items-center cursor-pointer 2xl:p-3 lg:p-2.5 p-2 ${
                      item.length !== index - 1
                        ? "border-b border-[#d3d3d3]"
                        : ""
                    } `}
                    id={`web-global-search-suggestion-item-pharmacy-${
                      index + 1
                    }`}
                  >
                    <img
                      src={`${ImageBaseUrl}${item?.image}`}
                      alt=""
                      className="w-[1.5vw] h-full"
                      onError={(e) => {
                        const target = e.target as HTMLImageElement; // Assert the type
                        if (target) {
                          target.src = defaultShoppingImage;
                        }
                      }}
                    />

                    <span className="text-[#707070] truncate font-Poppins-Medium font-size-16px">
                      {highlightMatch(item.name, searchVal)}
                    </span>
                  </div>
                ))}
              {searchedData?.brand && searchedData?.brand.length > 0 && (
                <div className="flex flex-row justify-between 2xl:p-3 lg:p-2.5 p-2 bg-[#efefef]">
                  <h4 className=" font-Poppins-SemiBold  font-size-16px">
                    Brands{" "}
                  </h4>
                  <button
                    type="button"
                    onClick={() => handleNavigate("pharmacy")}
                    className="theme-color-green font-Poppins-Medium font-size-16px underline"
                    id={`web-global-search-view-all-button-pharmacy`}
                  >
                    View all
                  </button>
                </div>
              )}

              {searchedData?.brand &&
                searchedData?.brand.length > 0 &&
                searchedData?.brand.slice(0, 2).map((item: any, index: any) => (
                  <div
                    key={index}
                    onClick={() =>
                      handleProductNavigate({ ...item, type: "brand" })
                    }
                    className={`flex flex-row gap-2 items-center cursor-pointer 2xl:p-3 lg:p-2.5 p-2 ${
                      item.length !== index - 1
                        ? "border-b border-[#d3d3d3]"
                        : ""
                    } `}
                    id={`web-global-search-suggestion-item-pharmacy-${
                      index + 1
                    }`}
                  >
                    <img
                      src={`${ImageBaseUrl}${item?.image}`}
                      alt=""
                      className="w-[1.5vw] h-full"
                      onError={(e) => {
                        const target = e.target as HTMLImageElement; // Assert the type
                        if (target) {
                          target.src = defaultShoppingImage;
                        }
                      }}
                    />

                    <span className="text-[#707070] truncate font-Poppins-Medium font-size-16px">
                      {highlightMatch(item.name, searchVal)}
                    </span>
                  </div>
                ))}

              {searchedData?.pharmacy && searchedData?.pharmacy.length > 0 && (
                <div className="flex flex-row justify-between 2xl:p-3 lg:p-2.5 p-2 bg-[#efefef]">
                  <h4 className=" font-Poppins-SemiBold  font-size-16px">
                    Pharmacy{" "}
                  </h4>
                  <button
                    type="button"
                    onClick={() => handleNavigate("pharmacy")}
                    className="theme-color-green font-Poppins-Medium font-size-16px underline"
                    id={`web-global-search-view-all-button-pharmacy`}
                  >
                    View all
                  </button>
                </div>
              )}
              {searchedData?.pharmacy &&
                searchedData?.pharmacy.length > 0 &&
                searchedData?.pharmacy
                  .slice(0, 2)
                  .map((item: any, index: any) => (
                    <div
                      key={index}
                      onClick={() =>
                        handleProductNavigate({ ...item, type: "pharmacy" })
                      }
                      className={`flex flex-row gap-2 items-center cursor-pointer 2xl:p-3 lg:p-2.5 p-2 ${
                        item.length !== index - 1
                          ? "border-b border-[#d3d3d3]"
                          : ""
                      } `}
                      id={`web-global-search-suggestion-item-pharmacy-${
                        index + 1
                      }`}
                    >
                      <img
                        src={`${ImageBaseUrl}${item?.image}`}
                        alt=""
                        className="w-[1.5vw] h-full"
                        onError={(e) => {
                          const target = e.target as HTMLImageElement; // Assert the type
                          if (target) {
                            target.src = defaultShoppingImage;
                          }
                        }}
                      />

                      <span className="text-[#707070] truncate font-Poppins-Medium font-size-16px">
                        {highlightMatch(item.name, searchVal)}
                      </span>
                    </div>
                  ))}
              {searchedData?.diagnosticProduct &&
                searchedData?.diagnosticProduct.length > 0 && (
                  <div className="flex flex-row justify-between 2xl:p-3 lg:p-2.5 p-2 bg-[#efefef]">
                    <h4 className=" font-Poppins-SemiBold  font-size-16px">
                      Diagnostic lab test{" "}
                    </h4>
                    <button
                      type="button"
                      onClick={() => handleNavigate("diagnostic lab test")}
                      className="theme-color-green font-Poppins-Medium font-size-16px underline"
                      id={`web-global-search-view-all-button-diagnostic-lab-test`}
                    >
                      View all
                    </button>
                  </div>
                )}
              {searchedData?.diagnosticProduct &&
                searchedData?.diagnosticProduct.length > 0 &&
                searchedData?.diagnosticProduct
                  .slice(0, 2)
                  .map((item: any, index: any) => (
                    <div
                      key={index}
                      onClick={() =>
                        handleProductNavigate({
                          ...item,
                          type: "diagnosticProduct",
                        })
                      }
                      className={`flex flex-row gap-2 items-center cursor-pointer 2xl:p-3 lg:p-2.5 p-2 ${
                        item.length !== index - 1
                          ? "border-b border-[#d3d3d3]"
                          : ""
                      }`}
                      id={`web-global-search-suggestion-item-diagnostic-lab-test-${
                        index + 1
                      }`}
                    >
                      <img
                        src={`${ImageBaseUrl}${item?.productImage}`}
                        alt=""
                        className="w-[1.5vw] h-full"
                        onError={(e) => {
                          const target = e.target as HTMLImageElement; // Assert the type
                          if (target) {
                            target.src = defaultShoppingImage;
                          }
                        }}
                      />

                      <span className="text-[#707070] truncate font-Poppins-Medium font-size-16px">
                        {highlightMatch(item.name, searchVal)}
                      </span>
                    </div>
                  ))}
              {searchedData?.diagnosticPackage &&
                searchedData?.diagnosticPackage.length > 0 && (
                  <div className="flex flex-row justify-between 2xl:p-3 lg:p-2.5 p-2 bg-[#efefef]">
                    <h4 className=" font-Poppins-SemiBold  font-size-16px">
                      Diagnostic packages{" "}
                    </h4>
                    <button
                      type="button"
                      onClick={() => handleNavigate("diagnostic packages")}
                      className="theme-color-green font-Poppins-Medium font-size-16px underline"
                      id={`web-global-search-view-all-button-diagnostic-packages`}
                    >
                      View all
                    </button>
                  </div>
                )}
              {searchedData?.diagnosticPackage &&
                searchedData?.diagnosticPackage.length > 0 &&
                searchedData?.diagnosticPackage
                  .slice(0, 2)
                  .map((item: any, index: any) => (
                    <div
                      key={index}
                      onClick={() =>
                        handleProductNavigate({
                          ...item,
                          type: "diagnosticPackage",
                        })
                      }
                      className={`flex flex-row gap-2 items-center cursor-pointer 2xl:p-3 lg:p-2.5 p-2 ${
                        item.length !== index - 1
                          ? "border-b border-[#d3d3d3]"
                          : ""
                      }`}
                      id={`web-global-search-suggestion-item-diagnostic-packages-${
                        index + 1
                      }`}
                    >
                      <img
                        src={`${ImageBaseUrl}${item?.image}`}
                        alt=""
                        className="w-[1.5vw] h-full"
                        onError={(e) => {
                          const target = e.target as HTMLImageElement; // Assert the type
                          if (target) {
                            target.src = defaultShoppingImage;
                          }
                        }}
                      />

                      <span className="text-[#707070] truncate font-Poppins-Medium font-size-16px">
                        {highlightMatch(item.name, searchVal)}
                      </span>
                    </div>
                  ))}
              {searchedData?.homecare && searchedData?.homecare.length > 0 && (
                <div className="flex flex-row justify-between 2xl:p-3 lg:p-2.5 p-2 bg-[#efefef]">
                  <h4 className=" font-Poppins-SemiBold  font-size-16px">
                    Home care services{" "}
                  </h4>
                  <button
                    type="button"
                    onClick={() => handleNavigate("homecare")}
                    className="theme-color-green font-Poppins-Medium font-size-16px underline"
                    id={`web-global-search-view-all-button-homecare`}
                  >
                    View all
                  </button>
                </div>
              )}
              {searchedData?.homecare &&
                searchedData?.homecare.length > 0 &&
                searchedData?.homecare
                  .slice(0, 2)
                  .map((item: any, index: any) => (
                    <div
                      key={index}
                      onClick={() =>
                        handleProductNavigate({ ...item, type: "homecare" })
                      }
                      className={`flex flex-row gap-2 items-center cursor-pointer 2xl:p-3 lg:p-2.5 p-2 ${
                        item.length !== index - 1
                          ? "border-b border-[#d3d3d3]"
                          : ""
                      }`}
                      id={`web-global-search-suggestion-item-homecare-${
                        index + 1
                      }`}
                    >
                      <img
                        src={`${ImageBaseUrl}${item?.image}`}
                        alt=""
                        className="w-[1.5vw] h-full"
                        onError={(e) => {
                          const target = e.target as HTMLImageElement; // Assert the type
                          if (target) {
                            target.src = defaultShoppingImage;
                          }
                        }}
                      />

                      <span className="text-[#707070] truncate font-Poppins-Medium font-size-16px">
                        {highlightMatch(item.name, searchVal)}
                      </span>
                    </div>
                  ))}
              {/* {context === "dashboard" && ( */}
              <>
                {searchedData?.doctor && searchedData?.doctor.length > 0 && (
                  <div className="flex flex-row justify-between 2xl:p-3 lg:p-2.5 p-2 bg-[#efefef]">
                    <h4 className=" font-Poppins-SemiBold  font-size-16px">
                      Doctors{" "}
                    </h4>
                    <button
                      type="button"
                      onClick={() => handleNavigate("doctor")}
                      className="theme-color-green font-Poppins-Medium font-size-16px underline"
                      id={`web-global-search-view-all-button-doctor`}
                    >
                      View all
                    </button>
                  </div>
                )}
                {searchedData?.doctor &&
                  searchedData?.doctor.length > 0 &&
                  searchedData?.doctor
                    .slice(0, 2)
                    .map((item: any, index: any) => (
                      <div
                        key={index}
                        onClick={() =>
                          handleProductNavigate({ ...item, type: "doctor" })
                        }
                        className={`flex flex-row gap-2 items-center cursor-pointer 2xl:p-3 lg:p-2.5 p-2 ${
                          item.length !== index - 1
                            ? "border-b border-[#d3d3d3]"
                            : ""
                        }`}
                        id={`web-global-search-suggestion-item-doctor-${
                          index + 1
                        }`}
                      >
                        <img
                          src={`${item?.coverPhoto}`}
                          alt=""
                          className="w-[1.5vw] h-full rounded-full object-cover"
                          onError={(e) => {
                            const target = e.target as HTMLImageElement; // Assert the type
                            if (target) {
                              target.src = defaultProfile;
                            }
                          }}
                        />

                        <span className="text-[#707070] truncate font-Poppins-Medium font-size-16px">
                          {highlightMatch(item.name, searchVal)}
                        </span>
                      </div>
                    ))}
              </>
              {/* )} */}
            </>
          ) : (
            <div
              className={`flex flex-row gap-2 items-center 2xl:p-3 lg:p-2.5 p-2 border-t border-[#d3d3d3]`}
            >
              <span className="text-[#707070] font-Poppins-Medium font-size-16px">
                No record found
              </span>
            </div>
          )}
        </div>
      )}
    </form>
  );
};

export default WebGlobalSearch;

const highlightMatch = (text: string, searchVal: string) => {
  if (!searchVal) return text;

  const regex = new RegExp(`(${searchVal})`, "gi");
  const parts = text.split(regex);

  return parts.map((part, index) =>
    part.toLowerCase() === searchVal.toLowerCase() ? (
      <span key={index} className="theme-color-green">
        {part}
      </span>
    ) : (
      <span key={index}>{part}</span>
    )
  );
};
